import React from 'react';
import { Bell, ExternalLink, AlertTriangle, Sparkles, Loader2, RefreshCw, Copy, CheckCircle2, Mail } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { policy } from '@/lib/api';

const SOURCE_COLORS = {
  DHA: 'bg-blue-100 text-blue-700',
  OMARA: 'bg-purple-100 text-purple-700',
  ACS: 'bg-teal-100 text-teal-700',
  LegEnd: 'bg-amber-100 text-amber-700',
};

export default function PolicyPulsePage() {
  const [alerts, setAlerts] = React.useState([]);
  const [stats, setStats] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [msg, setMsg] = React.useState('');

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([policy.alerts(), policy.stats()])
      .then(([a, s]) => { setAlerts(a); setStats(s); })
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  const refresh = async () => {
    setRefreshing(true); setMsg('');
    try {
      const r = await policy.refresh();
      setMsg(`Ingestion complete: ${r.inserted} new alert${r.inserted === 1 ? '' : 's'} from ${r.sources_checked} sources.`);
      load();
    } catch (e) { setMsg('Refresh failed — sources may be temporarily unavailable.'); }
    finally { setRefreshing(false); }
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-serif text-3xl font-bold tracking-tight text-[#1A3A5C]">Policy Pulse</h1>
          <div className="text-sm text-slate-500">Real feeds ingested every 6 hours · AI-summarised · impact-mapped to open matters</div>
        </div>
        <Button onClick={refresh} disabled={refreshing} variant="outline" className="h-10 gap-1.5 border-slate-200">
          {refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh now
        </Button>
      </div>

      {msg && <div className="mt-4 rounded-md bg-[#2ECC9A]/10 p-3 text-sm text-[#1A3A5C]">{msg}</div>}

      {stats && (
        <div className="mt-6 grid gap-3 md:grid-cols-4">
          {[
            { l: 'Alerts this week', v: stats.alerts_this_week },
            { l: 'Matters impacted', v: stats.matters_impacted },
            { l: 'Total alerts on file', v: stats.total_alerts },
            { l: 'Sources monitored', v: stats.sources_monitored },
          ].map((s) => (
            <Card key={s.l} className="border-slate-200"><CardContent className="p-4">
              <div className="text-xs uppercase tracking-widest text-slate-500">{s.l}</div>
              <div className="mt-1 font-serif text-2xl font-bold text-[#1A3A5C]">{s.v}</div>
            </CardContent></Card>
          ))}
        </div>
      )}

      {loading ? (
        <div className="mt-8 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
      ) : alerts.length === 0 ? (
        <div className="mt-8 rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-sm text-slate-500">
          No alerts yet. Click “Refresh now” to trigger an ingestion pass.
        </div>
      ) : (
        <div className="mt-8 space-y-3">
          {alerts.map((a) => (
            <Card key={a.id} className="border-slate-200">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Badge className={(SOURCE_COLORS[a.source] || 'bg-slate-100 text-slate-700') + ' text-[10px] uppercase tracking-wider'}>{a.source}</Badge>
                      <span className="text-xs text-slate-500">Published {a.published}</span>
                      {a.seed && <span className="text-[10px] uppercase tracking-wider text-slate-400">seed</span>}
                    </div>
                    <div className="mt-2 font-serif text-lg font-semibold text-[#1A3A5C]">{a.title}</div>
                    <div className="mt-1 text-sm text-slate-600">{a.summary}</div>
                    {a.subclasses_affected?.length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {a.subclasses_affected.map((s) => <Badge key={s} variant="secondary" className="bg-slate-100 text-slate-700 text-[10px]">Subclass {s}</Badge>)}
                      </div>
                    )}
                  </div>
                  <div className="shrink-0 text-right">
                    {a.impacted > 0 ? (
                      <div className="inline-flex items-center gap-1 rounded-md bg-amber-50 px-3 py-1.5 text-xs font-medium text-amber-800">
                        <AlertTriangle className="h-3 w-3" /> {a.impacted} matter{a.impacted !== 1 ? 's' : ''}
                      </div>
                    ) : (
                      <div className="inline-flex items-center gap-1 rounded-md bg-emerald-50 px-3 py-1.5 text-xs font-medium text-emerald-700">No open matters affected</div>
                    )}
                  </div>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  {a.url && <a href={a.url} target="_blank" rel="noreferrer"><Button variant="outline" className="h-9 gap-1.5 border-slate-200"><ExternalLink className="h-3.5 w-3.5" /> Read source</Button></a>}
                  <DraftCommsDialog alert={a} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function DraftCommsDialog({ alert }) {
  const [open, setOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [draft, setDraft] = React.useState(null);
  const [err, setErr] = React.useState('');
  const [copied, setCopied] = React.useState(false);

  const generate = async () => {
    setLoading(true); setErr(''); setDraft(null);
    try { setDraft(await policy.draftComms(alert.id)); }
    catch (e) { setErr(e?.response?.data?.detail || 'Draft failed'); }
    finally { setLoading(false); }
  };

  React.useEffect(() => { if (open && !draft && !loading) generate(); /* eslint-disable-next-line */ }, [open]);

  const copyAll = () => {
    if (!draft) return;
    const s = `Subject: ${draft.subject}\n\n${draft.body}`;
    navigator.clipboard.writeText(s);
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="h-9 gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"><Sparkles className="h-3.5 w-3.5" /> Draft client comms</Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Draft client email — {alert.title}</DialogTitle></DialogHeader>
        {loading && <div className="flex justify-center p-8"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>}
        {err && <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{err}</div>}
        {draft && (
          <div className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Subject</label>
              <Input defaultValue={draft.subject} className="mt-1 h-9" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-slate-500">Body</label>
              <Textarea rows={12} defaultValue={draft.body} className="mt-1 font-serif" />
            </div>
            {draft.impacted_matters?.length > 0 && (
              <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
                <div className="text-xs font-semibold uppercase tracking-widest text-amber-800">Send to — {draft.impacted_matters.length} client{draft.impacted_matters.length === 1 ? '' : 's'}</div>
                <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                  {draft.impacted_matters.map((m) => (
                    <span key={m.ref} className="rounded-md bg-white px-2 py-1 text-amber-900">{m.applicant} ({m.ref} · sc{m.subclass})</span>
                  ))}
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={copyAll} className="gap-1.5 border-slate-200">
                {copied ? <CheckCircle2 className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />} {copied ? 'Copied' : 'Copy email'}
              </Button>
              <Button onClick={generate} className="gap-1.5 bg-[#1A3A5C] text-white hover:bg-[#1A3A5C]/90"><Sparkles className="h-4 w-4" /> Regenerate</Button>
              <Button className="gap-1.5 bg-[#2ECC9A] text-[#1A3A5C] hover:bg-[#2ECC9A]/90" disabled title="Bulk send coming next"><Mail className="h-4 w-4" /> Send to all</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
